class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    this.performSearch();
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      this.performSearch();
    }
  }

  /**
   * Build URL for search request.
   * @param {boolean} usePredictive - true = /search/suggest (predictive_search object — has suggestions, collections, etc.)
   *                                  false = /search (standard search object — only products, articles, pages, collections from search.results)
   * @param {string} [query] - optional query string; if omitted reads current input value
   */
  setupURL(usePredictive = true, query) {
    const base = usePredictive
      ? FoxTheme.routes.predictive_search_url
      : FoxTheme.routes.search_url;
    const url = new URL(`${window.shopUrl}${base}`);
    let search_term = query !== undefined ? query : this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    url.searchParams.set('q', search_term);
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    if (!usePredictive) {
      url.searchParams.set('options[prefix]', 'last');
    }
    return url;
  }

  /**
   * Extract the raw query string from a search URL (strips product_type prefix if present).
   */
  extractQueryFromUrl(urlString) {
    const url = new URL(urlString);
    let q = url.searchParams.get('q') || '';
    const andIndex = q.indexOf(' AND ');
    if (andIndex !== -1 && q.startsWith('product_type:')) {
      q = decodeURIComponent(q.substring(andIndex + 5));
    }
    return q;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    this.performSearch();
  }

  /**
   * Main search flow:
   *   1) Predictive Search (/search/suggest) — returns suggestions, collections, products, articles, pages
   *      (configured by Search & Discovery app)
   *   2) If predictive returned NO results at all — fallback to Standard Search (/search)
   *      to try to get at least products
   */
  performSearch() {
    const predictiveUrl = this.setupURL(true).toString();
    if (this.cachedMap.has(predictiveUrl)) {
      this.renderSearchResults(this.cachedMap.get(predictiveUrl));
      this.setAttribute('results', '');
      return;
    }
    this.renderSectionFromFetch(predictiveUrl);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText);
    this.setAttribute('results', '');
  }

  /** Count product cards inside a parsed results container */
  hasProducts(contentElement) {
    if (!contentElement) return false;
    return contentElement.querySelectorAll('.product-card').length > 0;
  }

  /** Check if there are any non-product results (suggestions, collections, articles/pages) */
  hasOtherResults(contentElement) {
    if (!contentElement) return false;
    const collections = contentElement.querySelectorAll('.predictive-search-result--collections a');
    const articles = contentElement.querySelectorAll('.predictive-search-result--article a');
    const suggestions = contentElement.querySelectorAll('.predictive-search-result--queries a');
    return collections.length > 0 || articles.length > 0 || suggestions.length > 0;
  }

  /**
   * Inject the products block from a standard search response into a predictive search DOM.
   * Keeps suggestions, collections, articles/pages from predictive untouched.
   * Mutates predictiveDoc and returns the full HTML string.
   */
  injectProductsIntoPredictive(predictiveDoc, resultsId, standardDoc) {
    const predictiveResults = predictiveDoc.getElementById(resultsId);
    const standardResults = standardDoc.getElementById(resultsId);
    if (!predictiveResults || !standardResults) return null;

    // Get the products container from standard search (.search__box-products)
    const standardProductsContainer = standardResults.querySelector('.search__box-products');
    if (!standardProductsContainer || standardProductsContainer.querySelectorAll('.product-card').length === 0) {
      return null;
    }

    // Remove empty-state block from predictive results (e.g. "No results found" message)
    const emptyState = predictiveResults.querySelector('.empty-state');
    if (emptyState) emptyState.remove();

    // Find or create the products container in predictive results
    let predictiveProductsContainer = predictiveResults.querySelector('.search__box-products');
    if (predictiveProductsContainer) {
      // Replace its content with products from standard search
      predictiveProductsContainer.innerHTML = standardProductsContainer.innerHTML;
    } else {
      // No products container exists in predictive — we need to build the layout
      // Grab any existing "others" content (suggestions, collections, articles) that predictive returned
      const existingOthers = predictiveResults.querySelector('.search__box-others');
      const flexWrapper = predictiveResults.querySelector('.flex.w-full');

      if (flexWrapper) {
        // Flex wrapper exists (predictive had some results) — insert products into it
        const productsDiv = predictiveDoc.createElement('div');
        productsDiv.className = 'flex flex-col flex-grow search__box-products order-last md:order-first';
        productsDiv.innerHTML = standardProductsContainer.innerHTML;
        flexWrapper.insertBefore(productsDiv, flexWrapper.firstChild);
      } else {
        // No flex wrapper — predictive was completely empty; build full structure with products
        const othersHtml = existingOthers ? existingOthers.outerHTML : '';
        predictiveResults.innerHTML =
          '<div class="flex w-full flex-col gap-y-6 md:flex-row">' +
            '<div class="flex flex-col flex-grow search__box-products order-last md:order-first">' +
              standardProductsContainer.innerHTML +
            '</div>' +
            othersHtml +
          '</div>';
      }
    }

    // We injected products — remove 'no-products' class from all .search__box-others
    predictiveResults.querySelectorAll('.search__box-others.no-products').forEach((el) => {
      el.classList.remove('no-products');
    });

    return predictiveDoc.documentElement.outerHTML;
  }

  renderSectionFromFetch(primaryUrl) {
    this.setLoadingState(true);
    const resultsId = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const parser = new DOMParser();

    const finish = (html) => {
      this.renderSearchResults(html);
      this.cachedMap.set(primaryUrl, html);
      this.setLoadingState(false);
    };

    // 1) Predictive search (/search/suggest) — suggestions, collections, products, articles/pages
    fetch(primaryUrl)
      .then((response) => {
        if (!response.ok) throw new Error('Predictive search response was not ok');
        return response.text();
      })
      .then((predictiveHtml) => {
        const predictiveDoc = parser.parseFromString(predictiveHtml, 'text/html');
        const contentElement = predictiveDoc.getElementById(resultsId);

        // If predictive has products — show everything as-is, no second request needed
        if (this.hasProducts(contentElement)) {
          finish(predictiveHtml);
          return;
        }

        // Products are empty — fetch standard search to fill the products section
        // Suggestions, collections, articles from predictive will be preserved
        // Derive fallback URL from primaryUrl query (not live input) to avoid race conditions with fast typing
        const capturedQuery = this.extractQueryFromUrl(primaryUrl);
        const standardUrl = this.setupURL(false, capturedQuery).toString();
        return fetch(standardUrl)
          .then((res) => (res.ok ? res.text() : Promise.reject(new Error('Standard search failed'))))
          .then((standardHtml) => {
            const standardDoc = parser.parseFromString(standardHtml, 'text/html');

            // Try to inject only the products block from standard into predictive
            const mergedHtml = this.injectProductsIntoPredictive(predictiveDoc, resultsId, standardDoc);
            if (mergedHtml) {
              // Merged: predictive suggestions/collections + standard products
              finish(mergedHtml);
            } else {
              // Standard search also has no products — show predictive as-is (may have suggestions/collections only, or empty state)
              finish(predictiveHtml);
            }
          })
          .catch(() => {
            // Standard search request failed — show predictive result as-is
            finish(predictiveHtml);
          });
      })
      .catch((error) => {
        console.error('Error fetching search: ', error);
        this.setAttribute('error', 'Failed to load data');
        this.setLoadingState(false);
      });
  }

  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
